import axios, { AxiosRequestConfig } from 'axios';
import type {
  BodyOf,
  ParamsOf,
  QueryOf,
  ResponseOf,
  RouteContract,
} from '@enki/contracts';
import { apiClientId, apiURL } from './config';

type RequestPart<Key extends string, Value> = [Value] extends [undefined]
  ? { [Part in Key]?: never }
  : { [Part in Key]: Value };

export type ContractRequest<C extends RouteContract> = RequestPart<
  'params',
  ParamsOf<C>
> &
  RequestPart<'query', QueryOf<C>> &
  RequestPart<'body', BodyOf<C>> & {
    token?: string;
    headers?: AxiosRequestConfig['headers'];
    timeout?: AxiosRequestConfig['timeout'];
    withCredentials?: AxiosRequestConfig['withCredentials'];
    signal?: AxiosRequestConfig['signal'];
  };

export function buildContractPath(
  path: string,
  params?: Record<string, unknown>
): string {
  return path.replace(/\{([^}]+)\}/g, (_placeholder, name: string) => {
    const hasValue =
      params !== undefined && Object.prototype.hasOwnProperty.call(params, name);
    const value = hasValue ? params[name] : undefined;
    if (value === undefined || value === null) {
      throw new Error(`Missing path parameter "${name}" for ${path}`);
    }

    if (
      (typeof value !== 'string' &&
        typeof value !== 'number' &&
        typeof value !== 'boolean') ||
      String(value).length === 0
    ) {
      throw new Error(`Invalid path parameter "${name}" for ${path}`);
    }

    if (value === '.' || value === '..') {
      throw new Error(`Unsafe path parameter "${name}" for ${path}`);
    }

    return encodeURIComponent(String(value));
  });
}

function serializeContractQuery(query: unknown): string {
  if (query === undefined || query === null) return '';
  if (typeof query !== 'object' || Array.isArray(query)) {
    throw new Error('Contract query must be a flat object');
  }

  const search = new URLSearchParams();
  for (const [name, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (
      typeof value !== 'string' &&
      typeof value !== 'number' &&
      typeof value !== 'boolean'
    ) {
      throw new Error(`Contract query parameter "${name}" must be a scalar`);
    }
    search.append(name, String(value));
  }
  return search.toString();
}

function buildHeaders(
  requestHeaders: AxiosRequestConfig['headers'],
  token?: string
): AxiosRequestConfig['headers'] {
  const headers = {
    'x-client-id': apiClientId,
    ...requestHeaders,
  } as Record<string, unknown>;

  if (token) {
    for (const name of Object.keys(headers)) {
      if (name.toLowerCase() === 'authorization') delete headers[name];
    }
    headers.Authorization = `Bearer ${token}`;
  }

  return headers as unknown as AxiosRequestConfig['headers'];
}

export async function requestContract<C extends RouteContract>(
  contract: C,
  request: ContractRequest<C>
): Promise<ResponseOf<C>> {
  const response = await axios.request<ResponseOf<C>>({
    baseURL: apiURL,
    method: contract.method,
    url: buildContractPath(
      contract.path,
      request.params as Record<string, unknown> | undefined
    ),
    params: request.query,
    paramsSerializer: serializeContractQuery,
    data: request.body,
    headers: buildHeaders(request.headers, request.token),
    timeout: request.timeout,
    withCredentials: request.withCredentials,
    signal: request.signal,
  });

  return response.data;
}
